import { ArrowLeftOutlined, SendOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Flex, Empty, message, Spin, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs"
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getRequestComment, submitComment } from "../../services/requestServices.js";
import { getNotificationRecipients, hideApprovalStatus } from "../../utils/requestHelper.js";
import { triggerNotification } from "../requests/triggerNotification.js";
import { getStoredCompanyIdentifier } from "../../utils/companyIdentifier.js";

const { Title } = Typography;

function ChatRoom({ spaceData = {}, closeChatroom, showChatroom, requestDetail = {}, notificationTemplates = [], spaceEntitlements=[] }) {
    const { t } = useTranslation();
    const [currentMessage, setCurrentMessage] = useState(null)
    const [commentDetail, setCommentDetail] = useState({})
    const [comments, setComments] = useState([])
    const [loader, setLoader] = useState(false)

    const messageContainer = useRef()
    const abortControllerRef = useRef(new AbortController())
    const timerRef = useRef(null)
    const hasFetchedInitialRef = useRef(false)

    const { spaceId, workflowId, requestId } = useParams()
    const { userInfo, userGroups } = useSelector(state => state.user);
    const { spaceSpecificUsers = [], spaceSpecificGroups = [] } = useSelector((state) => state.userRoleGroupState)

    /* ---------------- FETCH COMMENTS ---------------- */

    const fetchAllComments = async () => {
        try {
            const response = await getRequestComment(
                requestId,
                abortControllerRef.current.signal
            )
            if (response.status === 200) {
                setComments(response?.data?.data || [])
            }
        } catch (error) {
        }
    }

    const fetchInitialComments = async () => {
        if (hasFetchedInitialRef.current) return
        hasFetchedInitialRef.current = true

        try {
            setLoader(true)
            const response = await getRequestComment(
                requestId,
                abortControllerRef.current.signal
            )
            if (response.status === 200) {
                setComments(response?.data?.data || [])
            }
        } finally {
            setLoader(false)
        }
    }

    /* ---------------- INITIAL LOAD ---------------- */

    useEffect(() => {
        hasFetchedInitialRef.current = false
        fetchInitialComments()

        return () => {
            abortControllerRef.current.abort()
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [requestId])

    /* ---------------- POLLING ---------------- */

    useEffect(() => {
        if (!showChatroom || !hasFetchedInitialRef.current) return

        timerRef.current = setInterval(() => {
            fetchAllComments()
        }, 7000)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [showChatroom])

    /* ---------------- AUTO SCROLL ---------------- */

    useEffect(() => {
        if (messageContainer && messageContainer.current) {
            setTimeout(() => {
                if (messageContainer.current) {
                    messageContainer.current.scrollTop =
                        messageContainer.current.scrollHeight
                }
            }, 100)
        }
    }, [comments])

    /* ---------------- SUBMIT MESSAGE ---------------- */

    const updateLogs = async () => {
        let postData = {
            ...commentDetail,
            spaceId,
            workflowId,
            requestId,
            data: [...comments]
        }

        if (!currentMessage) {
            message.warning(t("Message is required"))
            return
        }

        let newRecord = {
            comment: currentMessage,
            createdBy: userInfo.email,
            createdOn: dayjs().format("YYYY/MM/DD hh:mm a"),
            stepName: ""
        }

        postData.data.push(newRecord)
        let method = comments.length === 0 ? "POST" : "PUT"

        /* stop polling */
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }

        /* abort inflight fetch */
        abortControllerRef.current.abort()
        abortControllerRef.current = new AbortController()

        setComments([...comments, { ...newRecord }])
        setCurrentMessage(null)

        setTimeout(() => {
            messageContainer.current.scrollTop =
                messageContainer.current.scrollHeight
        }, 400)

        let resp = await submitComment(postData, method)

        if (resp.status === 200) {
            setCommentDetail(resp.data)

            if (showChatroom) {
                timerRef.current = setInterval(() => {
                    fetchAllComments()
                }, 7000)
            }
            let companyIdentifier = getStoredCompanyIdentifier();

            let chatRoomTemplate = notificationTemplates.find(template => template.notificationType === "CHATROOM_NOTIFICATION");
            if (chatRoomTemplate?.enabled) {
                let modInformation = {
                    ...requestDetail.value,
                    "Recipient": "@Recipient",
                    "Chatroom name": "",
                    "Chatroom message sender": userInfo.name,
                    "Chatroom message": currentMessage,
                    "Space name": spaceData?.name,
                    "Request Id": requestDetail?.caseId,
                    spaceId: spaceId,
                    redirectUrl: `/${companyIdentifier}/app/space/${spaceId}/approve/${workflowId}/${requestId}`,
                    "Due date": requestDetail.dueDate,
                    "Workflow step": requestDetail?.status
                }
                let recipientList = getNotificationRecipients(requestDetail.workflow, userInfo.email)
                if(recipientList.user?.length > 0 || recipientList.group?.length > 0){   
                    triggerNotification({
                        recipients: recipientList,
                        notificationParams: modInformation,
                        template: chatRoomTemplate,
                        userInfo: userInfo,
                        currentStepInfo: null,
                        spaceSpecificGroups: spaceSpecificGroups,
                        spaceSpecificUsers: spaceSpecificUsers
                        // uniqueApprovers, modInformation, reqTemplate, userInfo, nextStep, spaceSpecificGroups, spaceSpecificUsers
                    })
                }
            }
        }
    }

    const getNameInitial = () => {
        let name = userInfo.name;
        let names = name.split(' ');
        let firstName = names[0]?.charAt(0)?.toUpperCase();
        let lastName = names[1] ? names[1].charAt(0)?.toUpperCase() : "";
        return firstName + lastName;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            updateLogs();
        }
    };

    /* ---------------- JSX (UNCHANGED) ---------------- */

    return (
        <Spin spinning={loader} style={{ width: '100%', height: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                <div style={{ flexShrink: 0, paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                    <Flex gap={16}>
                        <ArrowLeftOutlined className="app-icon cursor-pointer" style={{ color: "#000", size: "20px" }} onClick={closeChatroom} />
                        <Title level={4} className='text-color-primary' style={{ margin: "0" }}>{t('Comments')}</Title>
                    </Flex>
                </div>

                <div className="chat-room-message-section" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none', minHeight: '0' }}>
                    {
                        (comments && comments.length > 0) ? (
                            <div className="message-container" ref={messageContainer}>
                                {
                                    comments.map(comment => {
                                        return (
                                            <div className="message-wrapper" data-float={comment.createdBy == userInfo.email ? "right" : "left"}>
                                                <div>
                                                    <div className="message">
                                                        {
                                                            comment.createdBy == userInfo.email ? (
                                                                <>
                                                                    <div className="message-text">{comment.comment}</div>
                                                                    <Avatar shape="circle" icon={<UserOutlined />} style={{ backgroundColor: "#FFF0F6", color: "#EB2F96", marginLeft: "8px" }} />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Avatar shape="circle" icon={<UserOutlined />} style={{ backgroundColor: "#E6F4FF", color: "#1677FF", marginRight: "8px" }} />
                                                                    <div className="message-text">{comment.comment}</div>
                                                                </>
                                                            )
                                                        }
                                                    </div>
                                                    <div className="sender">{comment.createdBy == userInfo.email ? "me" : comment.createdBy}. {comment.createdOn}</div>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        ) : (
                            <Empty description=" " style={{ paddingTop: 10 }}></Empty>
                        )
                    }
                </div>

                {
                    !hideApprovalStatus.includes(requestDetail?.status) && (spaceEntitlements.includes("Chatroom_Update") || userInfo.isAdmin) ?
                        <div className="chat-room-send-message" style={{ flexShrink: 0, marginTop: 'auto', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                            <div className="user-avatar">{getNameInitial()}</div>
                            <TextArea value={currentMessage} placeholder={t('Enter message')} onChange={(e) => setCurrentMessage(e.target.value)} onKeyDown={handleKeyDown} />
                            <div className="send-icon-container" onClick={updateLogs}>
                                <SendOutlined style={{ marginLeft: "2px" }} />
                            </div>
                        </div>
                        : null
                }
            </div>
        </Spin>
    )
}

export default ChatRoom
